import React, { useState, useRef } from 'react';
import { Button, Dropdown } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import MarkdownModal from './MarkdownModal';
import styles from './AIInsightsButton.module.css';

interface AIInsightsButtonProps<T> {
  data: T[];
  getLabel: (item: T) => string;
  getKey: (item: T) => string;
  getItemTitle: (item: T) => string;
  fetchData: (item: T) => Promise<string>;
  buttonText: string;
  modalTitle: string;
  modalLoadingText: string;
  errorMessage: string;
}

function AIInsightsButton<T>({
  data,
  getLabel,
  getKey,
  getItemTitle,
  fetchData,
  buttonText,
  modalTitle,
  modalLoadingText,
  errorMessage,
}: AIInsightsButtonProps<T>) {
  const [showModal, setShowModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState<T | null>(null);
  const [content, setContent] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const requestIdRef = useRef(0);

  const handleItemSelect = async (item: T) => {
    const currentRequestId = ++requestIdRef.current;
    setSelectedItem(item);
    setShowModal(true);
    setIsLoading(true);
    setContent(null);

    try {
      const result = await fetchData(item);
      if (currentRequestId === requestIdRef.current) {
        setContent(result);
      }
    } catch (error) {
      if (currentRequestId === requestIdRef.current) {
        setContent(errorMessage);
      }
    } finally {
      if (currentRequestId === requestIdRef.current) {
        setIsLoading(false);
      }
    }
  };

  if (data.length === 1) {
    return (
      <>
        <Button
          variant="secondary"
          className={styles.button}
          onClick={() => handleItemSelect(data[0])}
        >
          <FontAwesomeIcon icon={["fal", "sparkles"]} className={styles.icon} />
          {buttonText}
        </Button>
        <MarkdownModal
          show={showModal}
          onHide={() => setShowModal(false)}
          item={selectedItem}
          getItemTitle={getItemTitle}
          content={content}
          isLoading={isLoading}
          modalTitle={modalTitle}
          loadingText={modalLoadingText}
        />
      </>
    );
  }

  return (
    <>
      <Dropdown
        className={styles.button}
        onClick={(e: any) => e.stopPropagation()}
      >
        <Dropdown.Toggle variant="secondary" id="dropdown-ai-insights" className={styles.button}>
          <FontAwesomeIcon icon={["fal", "sparkles"]} className={styles.icon} />
          {buttonText}
        </Dropdown.Toggle>

        <Dropdown.Menu className={styles.dropdownMenu}>
          {data.map((item) => (
            <Dropdown.Item
              key={getKey(item)}
              onClick={() => handleItemSelect(item)}
              className={styles.dropdownItem}
            >
              {getLabel(item)}
            </Dropdown.Item>
          ))}
        </Dropdown.Menu>
      </Dropdown>
      <MarkdownModal
        show={showModal}
        onHide={() => setShowModal(false)}
        item={selectedItem}
        getItemTitle={getItemTitle}
        content={content}
        isLoading={isLoading}
        modalTitle={modalTitle}
        loadingText={modalLoadingText}
      />
    </>
  );
}

export default AIInsightsButton; 